import { FC, ReactNode, useState } from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";

import { useGetMyPermissionsQuery } from "../../../entities/me";
import { useLogoutMutation } from "../../../entities/auth";
import { localStorageService } from "../../../shared/api/localstorage-service";
import { Logo } from "../../../shared/ui/logo";
import { tokens } from "../../../shared/config/theme";
import { getNavItemByPath } from "../../../pages/home/model/admin-menu-items";

import { AdminSidebarNav } from "./admin-sidebar-nav";
import { AdminHeader } from "./admin-header";

const SIDEBAR_STORAGE_KEY = "rapl-admin-sidebar-collapsed";

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

interface AdminShellProps {
  children?: ReactNode;
}

export const AdminShell: FC<AdminShellProps> = ({ children }) => {
  const location = useLocation();
  const { data: permissionsData } = useGetMyPermissionsQuery();
  const [logout] = useLogoutMutation();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const permissions = permissionsData?.permissions || [];
  const currentItem = getNavItemByPath(location.pathname);
  const sidebarWidth = collapsed
    ? tokens.sidebar.collapsed
    : tokens.sidebar.expanded;

  const handleToggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // ignore quota / private mode
      }
      return next;
    });
  };

  const handleLogout = async () => {
    const token = await localStorageService.getToken();
    if (token) {
      logout(token);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Box
        component="aside"
        sx={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          display: "flex",
          flexDirection: "column",
          bgcolor: tokens.navy[950],
          borderRight: "1px solid",
          borderColor: "divider",
          transition: "width 0.2s ease, min-width 0.2s ease",
        }}
      >
        <Box
          sx={{
            height: tokens.header.height,
            minHeight: tokens.header.height,
            px: collapsed ? 1 : 2,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Logo compact={collapsed} />
        </Box>
        <AdminSidebarNav permissions={permissions} collapsed={collapsed} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AdminHeader
          title={currentItem?.label || "Администрирование"}
          collapsed={collapsed}
          onToggleCollapsed={handleToggleCollapsed}
          onLogout={handleLogout}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
